'use client';

import { useEffect } from 'react';

import useSWR from 'swr';

import { ToastAction, useToast } from '@quick/components/Toaster';

import { useAppTranslation } from '@/app/i18n/client';
import Refresh from '@/assets/refresh.svg';
import { envs } from '@/layers/core/data/const';

/** Written at build time by `app/version.json/route.ts`, next to the bundle it describes. */
export const VERSION_URL = '/version.json';

const readServedVersion = async (url: string): Promise<string | null> => {
  // no-store: the browser cache must never answer with the version that is already running.
  const response = await fetch(url, { cache: 'no-store' });
  if (!response.ok) return null;
  const { hash } = (await response.json()) as { hash?: string };
  return hash || null;
};

/**
 * Tells the user a newer build is being served. It compares the build that is running with the
 * one the same origin serves now, so it can only fire after a deploy and a reload clears it.
 */
export function VersionAlert() {
  const { t } = useAppTranslation('common');
  const { toast } = useToast();
  const running = envs.pwaVersion;

  const { data: served } = useSWR(
    running ? VERSION_URL : null,
    readServedVersion,
    {
      refreshInterval: 300_000, // Check every 5 minutes
      dedupingInterval: 300_000,
      shouldRetryOnError: false,
    }
  );

  const isOutdated = Boolean(running && served && served !== running);

  useEffect(() => {
    if (isOutdated) {
      toast({
        title: t('versionAlert.title'),
        description: t('versionAlert.message'),
        variant: 'info',
        action: (
          <ToastAction
            altText={t('versionAlert.refreshButton')}
            onClick={handleRefresh}
          >
            <Refresh className="size-6" aria-hidden="true" />
          </ToastAction>
        ),
      });
    }
  }, [isOutdated, t, toast]);

  return null;
}

const handleRefresh = () => {
  window.location.reload();
};
